import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { SubPageHeader } from './SubPageHeader';
import { useRegisterOverlay } from '@/stores/overlayStore';

export interface FullScreenSheetProps {
  /** Title rendered centered in the sticky top nav. */
  title: string;
  /** Called when the user taps the back chevron or presses Escape. */
  onClose: () => void;
  /** Leading affordance: the back chevron (default) or a ✕. */
  leading?: 'back' | 'close';
  /** Optional trailing header control (e.g. an Edit pencil). */
  rightAction?: ReactNode;
  /**
   * Pinned under the scrolling body (a primary action). Reserves the
   * home-indicator inset itself, outside the flex scroller, so iOS Safari
   * dropping a scroller's bottom padding cannot hide it.
   */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Full-screen modal-style overlay that slides in from the bottom of the
 * stacking context. Used by Settings for Privacy Policy, Terms of
 * Service, and FAQ so we can keep the user inside the app instead of
 * routing them to a standalone page.
 *
 * Mirrors the Yaruta Design System v1 frames 8315:71869 (Privacy) and
 * 8315:72075 (Terms) — sticky top nav with a centered title, back
 * chevron on the left, scrollable body.
 *
 * The nav is `SubPageHeader`, the same one the Settings sub-pages render.
 * It used to be a local 20px-bold variant whose centre slot truncated, so
 * Help & About read as a different screen family from Account/Household
 * and "Frequently Asked Questions" lost its tail (YAR-389).
 */
export function FullScreenSheet({
  title,
  onClose,
  leading = 'back',
  rightAction,
  footer,
  children,
}: FullScreenSheetProps) {
  // Mounted means open: pauses (and hides) the bottom toast (YAR-476).
  useRegisterOverlay(true);

  // Close on Escape and lock body scroll while open so the underlying
  // Settings list can't scroll behind the sheet.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-background flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <SubPageHeader
        title={title}
        onBack={onClose}
        leading={leading}
        rightAction={rightAction}
      />

      <div
        className={[
          'flex-1 overflow-y-auto px-5 pt-5',
          footer ? 'pb-5' : 'pb-[max(1.25rem,env(safe-area-inset-bottom))]',
        ].join(' ')}
      >
        {children}
      </div>

      {footer && (
        <div className="shrink-0 px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {footer}
        </div>
      )}
    </div>
  );
}
